import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getGuest, signOutGuest, isIpadMode } from "@/lib/guest";

/**
 * Individual-guest heartbeat. Every ~15 s it hits /public/guest-validate; on 404 the
 * guest is signed out and redirected. Does nothing in iPad mode (CabinView polls its own
 * session token instead).
 */
export function useGuestSessionWatch({ intervalMs = 15000 } = {}) {
  const navigate = useNavigate();
  useEffect(() => {
    if (isIpadMode()) return;
    let cancelled = false;

    const check = async () => {
      const g = getGuest();
      if (!g.id) return;
      try {
        await api.get("/public/guest-validate", { params: { guest_id: g.id } });
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        if ([400, 401, 403, 404].includes(status)) {
          toast.error("Your session has ended. Please sign in again.");
          signOutGuest();
          navigate("/", { replace: true });
        }
      }
    };

    // Immediate check + interval + refresh when tab regains focus.
    check();
    const timer = setInterval(check, intervalMs);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(timer); document.removeEventListener("visibilitychange", onVis); };
  }, [intervalMs, navigate]);
}
